import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { UserService } from '../api/api_pb';
import { useClient } from '../connect_api';

interface AddUserModalProps {
    isOpen: boolean;
    onClose: () => void;
    onUserCreated: () => void;
}

const AddUserModal: React.FC<AddUserModalProps> = ({ isOpen, onClose, onUserCreated }) => {
    const userClient = useClient(UserService);
    const [formData, setFormData] = useState({
        email: '',
        name: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const resetFormData = () => {
        setFormData({
            email: '',
            name: '',
        });
    };

    const handleCreate = async () => {
        if (!formData.email.trim() || !formData.name.trim()) {
            toast.error('Email and name are required');
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            toast.error('Please enter a valid email address');
            return;
        }

        setIsSubmitting(true);
        try {
            await userClient.createUser({
                email: formData.email.trim(),
                name: formData.name.trim(),
            });

            toast.success('User created successfully! An invitation email has been sent.');
            resetFormData();
            onUserCreated();
            onClose();
        } catch (error: any) {
            if (error.message.includes('duplicate') || error.message.includes('already exists')) {
                toast.error('A user with this email already exists');
            } else {
                toast.error(`Failed to create user: ${error.message}`);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        resetFormData();
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg w-96">
                <h2 className="text-xl font-bold mb-4">Add Admin User</h2>

                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="border border-gray-300 p-2 w-full mb-4 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Enter email address..."
                    disabled={isSubmitting}
                />

                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="border border-gray-300 p-2 w-full mb-4 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Enter display name..."
                    disabled={isSubmitting}
                />

                <p className="text-sm text-gray-500 mb-4">
                    The new user will receive an invitation email with a link to set their password.
                </p>

                <div className="flex justify-end gap-2">
                    <button
                        onClick={handleClose}
                        className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 transition-colors"
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleCreate}
                        className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Creating...' : 'Create User'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddUserModal;
